import { Component, OnInit, OnDestroy, ViewChild, HostListener, Input, AfterViewInit, ViewChildren, Output, EventEmitter } from '@angular/core';
import * as Chart from 'chart.js';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { PaygService } from '../../../services/payg.service';
import { PaygPaymentTypeModel } from '../../../models/payg/payg-payment-type.model';
import { getQueryParamFromAncestorRoute, getParamFromAncestorRoute } from '../../../utilities/route.utilities';
import { BillingService } from '../../../services/billing.service';

@Component({
    selector: 'cumlative-account-balance-chart',
    template: '<canvas id="basechart" #basechart></canvas>',
    styles: [':host { width: 100%; position: relative; display:block; }'],
    host: { 'class': 'block-detail' }
})
export class CumulativeAccountBalanceChartComponent implements AfterViewInit, OnDestroy {

    @ViewChildren('basechart') basechart: any;
    @Input() data: PaygPaymentTypeModel[];
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    chartData: any;
    cumulativeBalance: any;
    positiveChartData: any[] = [];
    negativeChartData: any[] = [];
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    timePeriod: number = 12;
    siteId: number;
    @Input() hasData: boolean = false;
    @Output() hasDataChange = new EventEmitter<boolean>();

    @Input() hasLoading: boolean = true;
    @Output() hasLoadingChange = new EventEmitter<boolean>();

    constructor(
        private billingService: BillingService,
        private route: ActivatedRoute,
        private router: Router
    ) { }

    ngAfterViewInit(): void {
        this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        });
    }

    onInitialise() {

        this.clientId = getParamFromAncestorRoute(this.route, 'clientId');
        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId');
        this.timePeriod = getQueryParamFromAncestorRoute(this.route, 'timePeriod') == null ? 12 : getQueryParamFromAncestorRoute(this.route, 'timePeriod');

        this.billingService.getAccountBalance(this.clientId, this.siteId, this.timePeriod).then((result) => {
            this.chartData = [];
            this.chartData = this.processData(result);
            this.hasDataChange.emit(this.chartData);
            this.hasLoadingChange.emit(!this.chartData || this.chartData.labels.length == 0);
            this.createChart();
        }).catch(reason => {
            this.hasDataChange.emit(false);
            this.hasLoadingChange.emit(false);
        });

    }

    processData(data: any[]) {

        let graphData = {
            labels: [],
            negative: [],
            positive: []
        };

        for (var i = 0; i < data.length; i++) {

            if (data && data.length > 0) graphData.labels.push(this.pipe.transform(data[i].date, 'MM/yy'));
            graphData.negative.push(data[i].negativeBalance);
            graphData.positive.push(data[i].positiveBalance);
        }

        return (graphData);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    private getChartConfig(): any {
        let config = {
            type: 'horizontalBar',
            options: {
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            var allData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex].data : [];
                            var tooltipLabel: any = data != null && data.labels != null && tooltipItem != null && tooltipItem.index != null ? data.labels[tooltipItem.index] : [];
                            var tooltipData: any = allData != null && allData.length > 0 && tooltipItem != null && tooltipItem.index != null ? allData[tooltipItem.index] : [];

                            if (tooltipData < 0) {
                                return tooltipLabel + ' Negative £' + tooltipData;
                            }
                            if (tooltipData > 0) {
                                return tooltipLabel + ' Positive £' + tooltipData;
                            }
                        }
                    }
                },
                scales: {
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Value (£)',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        gridLines: {
                            display: true
                        }
                    }],
                    yAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: 'Month',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            barThickness: 8,
                            id: "y-axis-0",
                            position: 'left',
                            gridLines: {
                                display: false
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        let datasets = [] as any;

        if (this.chartData) {
            datasets.push({
                label: 'Debt balance',
                yAxisID: 'y-axis-0',
                data: this.chartData.positive,
                backgroundColor: '#009aa3'
            });

            datasets.push({
                label: 'Credit balance',
                yAxisID: 'y-axis-0',
                data: this.chartData.negative,
                backgroundColor: '#00539f'
            });
        }
        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                labels: this.chartData.labels,
                datasets: datasets
            }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);

    }
}
